<script setup lang="ts">
import { ENTITIES, MODULES, REPORTS, ROOT_PARAMETRES_ENTITIES, ROOT_PARAMETRES_MODULES } from '~/data/maquette'
import { canAccessModule } from '~/data/permissions'

const view = useCurrentView()
const { expandedModule, expandedGroup } = useNav()
const currentUser = useCurrentUser()

const rootKey = '__root_parametres__'
const stripNumber = (label: string): string => label.replace(/^\d+\.\s*/, '')
const showDashboard = computed(() => canAccessModule(currentUser.value, 'tableauDeBord'))
const modules = computed(() => Object.entries(MODULES).filter(([key]) => !ROOT_PARAMETRES_MODULES.includes(key) && canAccessModule(currentUser.value, key)))
const financeParametres = MODULES.finance!.groups.find(g => g.key === 'parametres')
const showFinanceParametres = computed(() => !!financeParametres && canAccessModule(currentUser.value, 'finance'))
const visibleRootModules = computed(() => ROOT_PARAMETRES_MODULES.filter(mk => canAccessModule(currentUser.value, mk)))
const visibleRootEntities = computed(() => ROOT_PARAMETRES_ENTITIES.filter(ek => canAccessModule(currentUser.value, ENTITIES[ek]!.module)))
const showParametresSection = computed(() => visibleRootModules.value.length > 0 || showFinanceParametres.value || visibleRootEntities.value.length > 0)

const isEntityActive = (key: string): boolean =>
  (view.value.type === 'entity' || view.value.type === 'detail') && view.value.key === key

const financeCount = computed(() => financeParametres
  ? financeParametres.entities.reduce((s, ek) => s + entityRows(ek).length, 0)
  : 0)

/** Pied de menu : l'utilisateur connecté. */
const userDisplay = computed(() => {
  const u = currentUser.value
  if (!u) return null
  const p = findById('personnes', u.personneId)
  const name = p ? `${p.prenoms} ${p.nom}` : u.login
  const initials = p ? ((String(p.prenoms)[0] || '') + (String(p.nom)[0] || '')).toUpperCase() : u.login.slice(0, 2).toUpperCase()
  return { initials, name, role: u.roles.length > 0 ? u.roles.join(', ') : 'Aucun rôle' }
})
</script>

<template>
  <aside
    class="sidebar"
    aria-label="Menu latéral"
  >
    <div class="brand">
      <div
        class="brand-logo"
        aria-hidden="true"
      >
        R
      </div>
      <div class="word">
        <div class="mark">
          Registre
        </div>
        <div class="sub">
          Gestion scolaire
        </div>
      </div>
      <button
        class="sidebar-collapse"
        title="Réduire le menu"
        aria-label="Réduire ou fermer le menu"
        @click="toggleSidebar()"
      >
        <svg
          viewBox="0 0 24 24"
          class="icon icon-sm"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        ><polyline points="15 5 9 12 15 19" /></svg>
      </button>
    </div>

    <nav
      id="nav"
      class="side-nav"
      aria-label="Navigation principale"
    >
      <div
        v-if="showDashboard"
        :class="['nav-top-item', { active: view.type === 'dashboard' }]"
        @click="goTo('dashboard')"
      >
        <AppIcon name="layout-dashboard" /><span class="nav-top-label">Tableau de bord</span>
      </div>

      <div
        v-for="[modKey, mod] in modules"
        :key="modKey"
        class="nav-group"
      >
        <div
          class="nav-group-label"
          @click="goTo('module', modKey)"
        >
          <span
            :class="['grp-chevron', { open: expandedModule === modKey }]"
            @click.stop="toggleModuleGroup(modKey)"
          >›</span>
          <span :class="['nav-grp-text', { 'grp-active': view.type === 'module' && view.key === modKey }]">{{ stripNumber(mod.label) }}</span><span
            v-if="modKey === 'caisse' && caissePendingCount() > 0"
            class="count count-alert"
          >{{ caissePendingCount() }}</span>
        </div>
        <div
          v-if="expandedModule === modKey"
          class="nav-tree"
        >
          <NavEntityItem
            v-for="ek in mod.entities"
            :key="ek"
            :entity-key="ek"
          />
          <div
            v-for="rk in mod.reports"
            :key="rk"
            :class="['nav-item', { active: view.type === 'report' && view.key === rk }]"
            @click="goTo('report', rk)"
          >
            <AppIcon
              :name="REPORTS[rk]!.icon"
              extra="icon-sm"
            /><span class="nav-item-label">{{ REPORTS[rk]!.label }}</span><span
              v-if="rk === 'caissePending' && caissePendingCount() > 0"
              class="count count-alert"
            >{{ caissePendingCount() }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="showParametresSection"
        class="nav-group"
      >
        <div
          class="nav-group-label"
          @click="toggleModuleGroup(rootKey)"
        >
          <span
            :class="['grp-chevron', { open: expandedModule === rootKey }]"
            @click.stop="toggleModuleGroup(rootKey)"
          >›</span>
          <span class="nav-grp-text">Paramètres</span>
        </div>
        <div
          v-if="expandedModule === rootKey"
          class="nav-tree"
        >
          <template
            v-for="mk in visibleRootModules"
            :key="mk"
          >
            <div
              :class="['nav-item', { active: view.type === 'module' && view.key === mk }]"
              @click="goTo('module', mk)"
            >
              <span
                :class="['grp-chevron', { open: expandedGroup === `${rootKey}:${mk}` }]"
                @click.stop="toggleGroup(`${rootKey}:${mk}`)"
              >›</span>
              <AppIcon
                :name="MODULES[mk]!.icon"
                extra="icon-sm"
              /><span class="nav-item-label">{{ stripNumber(MODULES[mk]!.label) }}</span>
            </div>
            <div
              v-if="expandedGroup === `${rootKey}:${mk}`"
              class="nav-tree"
              style="margin-left:16px;"
            >
              <NavEntityItem
                v-for="ek in MODULES[mk]!.entities"
                :key="ek"
                :entity-key="ek"
              />
            </div>
          </template>

          <template v-if="financeParametres && showFinanceParametres">
            <div
              :class="['nav-item', { active: view.type === 'group' && view.key === 'finance' && view.id === 'parametres' }]"
              @click="goTo('group', 'finance', 'parametres')"
            >
              <span
                :class="['grp-chevron', { open: expandedGroup === 'finance:parametres' }]"
                @click.stop="toggleGroup('finance:parametres')"
              >›</span>
              <AppIcon
                :name="financeParametres.icon"
                extra="icon-sm"
              /><span class="nav-item-label">{{ financeParametres.label }}</span><span class="count">{{ financeCount }}</span>
            </div>
            <div
              v-if="expandedGroup === 'finance:parametres'"
              class="nav-tree"
              style="margin-left:16px;"
            >
              <NavEntityItem
                v-for="ek in financeParametres.entities"
                :key="ek"
                :entity-key="ek"
              />
            </div>
          </template>

          <div
            v-for="ek in visibleRootEntities"
            :key="ek"
            :class="['nav-item', 'nav-item-tall', { active: isEntityActive(ek) }]"
            :title="MODULES[ENTITIES[ek]!.module] ? `Rattaché au module ${stripNumber(MODULES[ENTITIES[ek]!.module]!.label)}` : undefined"
            @click="goTo('entity', ek)"
          >
            <span class="grp-chevron-spacer" />
            <AppIcon
              :name="ENTITIES[ek]!.icon"
              extra="icon-sm"
            />
            <div class="nav-item-stack">
              <span class="nav-item-label">{{ ENTITIES[ek]!.labelPlural }}</span>
              <span
                v-if="MODULES[ENTITIES[ek]!.module]"
                class="nav-item-origin"
              >Module {{ stripNumber(MODULES[ENTITIES[ek]!.module]!.label) }}</span>
            </div>
            <span class="count">{{ entityRows(ek).length }}</span>
          </div>
        </div>
      </div>

      <div
        class="nav-sep"
        style="height:1px;background:var(--rule);margin:8px 12px;"
      />
      <div
        :class="['nav-top-item', { active: view.type === 'guide' }]"
        @click="goTo('guide')"
      >
        <AppIcon name="help-circle" /><span class="nav-top-label">Mode d'emploi</span>
      </div>
    </nav>

    <div
      id="sidebar-footer"
      class="sidebar-footer"
    >
      <template v-if="userDisplay">
        <div class="avatar">
          {{ userDisplay.initials }}
        </div>
        <div class="who">
          <div class="name">
            {{ userDisplay.name }}
          </div>
          <div class="role">
            {{ userDisplay.role }}
          </div>
        </div>
        <button
          class="btn-icon"
          title="Se déconnecter"
          aria-label="Se déconnecter"
          style="margin-left:auto; flex:none;"
          @click="logout()"
        >
          <AppIcon name="log-out" />
        </button>
      </template>
    </div>
  </aside>
</template>
